import React, { ReactNode, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import { asUnified, ut } from '@devnw/theme'
import {
  CrudTable,
  CrudForm,
  CrudSecondaryButton,
  CrudDangerButton,
  CrudActionsRow,
  PageStack,
  PageTitle,
  SectionBlock,
  SectionTitle,
  TenantGateText,
} from '../components/crud'
import type { CrudColumn, CrudField } from '../components/crud'
import { PamError, PamConfirmDialog } from '../components/PamStateComponents'
import { useResource } from '../resource/useResource'
import type { User } from '../api/types'
import type {
  AdminUserItem,
  AdminUserActiveThreatHistoryPoint,
  AdminUserRiskHistoryPoint,
} from '../../../gen/client/react/client'
import { useAdminGenClient } from '../api/genClient'
import { useActiveTenant } from '../layout/TenantSwitcher'
import { UserSecurityHistoryChart } from '../components/UserSecurityHistoryChart'

type UserRow = User & {
  created_at?: string
  protected?: boolean
  risk_level?: string
  active_threat_level?: string
}

type SecurityHistoryState = {
  userID: string
  riskPoints: AdminUserRiskHistoryPoint[]
  activeThreatPoints: AdminUserActiveThreatHistoryPoint[]
  loading: boolean
  error: string | null
  nextCursor: string
  loadingMore: boolean
  rangeFrom: string
  rangeTo: string
}

const ROLE_OPTIONS = ['user', 'manager', 'admin'] as const

const columns: CrudColumn<UserRow>[] = [
  { key: 'email', header: 'Email' },
  { key: 'name', header: 'Name', render: ({ name }) => name ?? '—' },
  {
    key: 'roles',
    header: 'Roles',
    render: ({ roles }) => roles.join(', ') || '—',
  },
  {
    key: 'created_at',
    header: 'Created',
    render: ({ created_at: createdAt }) => createdAt ?? '—',
  },
  {
    key: 'risk_level',
    header: 'Risk',
    render: ({ risk_level: riskLevel }) => riskLevel ?? '—',
  },
  {
    key: 'active_threat_level',
    header: 'Active threat',
    render: ({ active_threat_level: threatLevel }) => threatLevel ?? '—',
  },
]

const createFields: CrudField[] = [
  { name: 'email', label: 'Email', type: 'text', required: true },
  { name: 'name', label: 'Name', type: 'text' },
  { name: 'password', label: 'Password', type: 'password', required: true },
  { name: 'role_user', label: 'Role: user', type: 'checkbox' },
  { name: 'role_manager', label: 'Role: manager', type: 'checkbox' },
  { name: 'role_admin', label: 'Role: admin', type: 'checkbox' },
]

/**
 * Map the generated AdminUserItem (camelCase) → UserRow (snake_case) so the
 * existing column/form/edit logic stays unchanged.
 *
 * `protected` is NOT declared on the generated AdminUserItem TS type (the
 * OpenAPI schema backing the admin-users list/get endpoints omits it — see
 * admin_users_server.go's userListItemJSON/userRespJSON, both of which DO
 * serialize a `protected` boolean on the wire; this is a backend/codegen
 * schema gap, out of scope for this UI migration). The field name itself has
 * no snake/camel mismatch ("protected" either way), so the previous
 * `u as unknown as { protected?: boolean }` cast was not silently dropping
 * data — but `as unknown as` is exactly the risky pattern the Gate-6
 * wire-mapping directive calls out to watch for, so it is replaced here with
 * a documented intersection type that only widens (never re-types) the real
 * AdminUserItem shape, keeping this call site the first to break loudly if
 * AdminUserItem and the wire response ever diverge on a field they share.
 */
type AdminUserItemWithProtected = AdminUserItem & { protected?: boolean }

function toUserRow(u: AdminUserItemWithProtected): UserRow {
  return {
    id: u.id,
    email: u.email,
    name: u.name,
    roles: u.roles ?? [],
    email_verified: u.emailVerified,
    risk_level: u.riskLevel,
    active_threat_level: u.activeThreatLevel,
    protected: u.protected,
  }
}

const InlineEditRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => ut(asUnified(theme)).space('xs')};
  flex-wrap: wrap;
`

const RoleCheckLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => ut(asUnified(theme)).space('xxs')};
  min-height: 44px;
  font-size: ${({ theme }) => asUnified(theme).font?.sizes?.sm ?? '0.875rem'};
  color: ${({ theme }) => asUnified(theme).text?.primary ?? '#0f172a'};
  cursor: pointer;
`

const InlineLabel = styled.label`
  font-size: ${({ theme }) => asUnified(theme).font?.sizes?.sm ?? '0.875rem'};
  color: ${({ theme }) => asUnified(theme).text?.primary ?? '#0f172a'};
`

const InlineInput = styled.input`
  min-height: 44px;
  padding: ${({ theme }) =>
    `${ut(asUnified(theme)).space('xs')} ${ut(asUnified(theme)).space('sm')}`};
  font-size: ${({ theme }) => asUnified(theme).font?.sizes?.sm ?? '0.875rem'};
  font-family: inherit;
  color: ${({ theme }) => asUnified(theme).text?.primary ?? '#0f172a'};
  background: ${({ theme }) => asUnified(theme).bg?.canvas ?? '#ffffff'};
  border: 1px solid
    ${({ theme }) => ut(asUnified(theme)).borderColor() ?? '#cbd5e1'};
  border-radius: ${({ theme }) => ut(asUnified(theme)).radius() ?? 4}px;

  &:focus-visible {
    outline: 2px solid
      ${({ theme }) => asUnified(theme).colors?.primary ?? '#1a56db'};
    outline-offset: 1px;
  }
`

export const Users = (): JSX.Element => {
  const { tenant } = useActiveTenant()
  const client = useAdminGenClient()

  const [editingId, setEditingId] = useState<string | null>(null)
  const [editingRoles, setEditingRoles] = useState<string[]>([])
  const [resetId, setResetId] = useState<string | null>(null)
  const [resetPwd, setResetPwd] = useState('')
  const [actionError, setActionError] = useState<string | null>(null)
  const [pendingDelete, setPendingDelete] = useState<UserRow | null>(null)
  const [historyUser, setHistoryUser] = useState<UserRow | null>(null)
  const [historyReload, setHistoryReload] = useState(0)
  const [history, setHistory] = useState<SecurityHistoryState>({
    userID: '',
    riskPoints: [],
    activeThreatPoints: [],
    loading: false,
    error: null,
    nextCursor: '',
    loadingMore: false,
    rangeFrom: '',
    rangeTo: '',
  })

  useEffect(() => {
    if (!historyUser) return undefined
    let active = true
    const rangeTo = new Date().toISOString()
    const rangeFrom = new Date(
      Date.parse(rangeTo) - 30 * 24 * 60 * 60 * 1000
    ).toISOString()
    setHistory({
      userID: historyUser.id,
      riskPoints: [],
      activeThreatPoints: [],
      loading: true,
      error: null,
      nextCursor: '',
      loadingMore: false,
      rangeFrom,
      rangeTo,
    })
    client
      .adminGetUserSecurityHistory({
        tenantId: tenant,
        userId: historyUser.id,
        from: rangeFrom,
        to: rangeTo,
      })
      .then((response) => {
        if (!active) return
        setHistory({
          userID: historyUser.id,
          riskPoints: response.riskPoints ?? [],
          activeThreatPoints: response.activeThreatPoints ?? [],
          loading: false,
          error: null,
          nextCursor: response.nextCursor ?? '',
          loadingMore: false,
          rangeFrom,
          rangeTo,
        })
      })
      .catch((error: unknown) => {
        if (!active) return
        setHistory({
          userID: historyUser.id,
          riskPoints: [],
          activeThreatPoints: [],
          loading: false,
          error:
            error instanceof Error ? error.message : 'History request failed',
          nextCursor: '',
          loadingMore: false,
          rangeFrom,
          rangeTo,
        })
      })
    return () => {
      active = false
    }
  }, [client, historyReload, historyUser, tenant])

  const handleLoadOlderHistory = (): void => {
    if (!historyUser || !history.nextCursor || history.loadingMore) return
    const userID = historyUser.id
    const cursor = history.nextCursor
    const rangeFrom = history.rangeFrom
    const rangeTo = history.rangeTo
    setHistory((current) => ({ ...current, loadingMore: true, error: null }))
    client
      .adminGetUserSecurityHistory({
        tenantId: tenant,
        userId: userID,
        cursor,
        from: rangeFrom,
        to: rangeTo,
      })
      .then((response) => {
        setHistory((current) => {
          if (
            current.userID !== userID ||
            current.rangeFrom !== rangeFrom ||
            current.rangeTo !== rangeTo
          ) {
            return current
          }
          return {
            ...current,
            riskPoints: [...current.riskPoints, ...(response.riskPoints ?? [])],
            activeThreatPoints: [
              ...current.activeThreatPoints,
              ...(response.activeThreatPoints ?? []),
            ],
            nextCursor: response.nextCursor ?? '',
            loadingMore: false,
          }
        })
      })
      .catch((error: unknown) => {
        setHistory((current) => {
          if (
            current.userID !== userID ||
            current.rangeFrom !== rangeFrom ||
            current.rangeTo !== rangeTo
          ) {
            return current
          }
          return {
            ...current,
            loadingMore: false,
            error:
              error instanceof Error ? error.message : 'History request failed',
          }
        })
      })
  }

  const rs = useResource<UserRow>({
    list: async () => {
      if (tenant === 'system') return []
      const resp = await client.adminListUsers({ tenantId: tenant })
      return (resp.users ?? []).map(toUserRow)
    },
    remove: async (id) => {
      await client.adminDeleteUser({ tenantId: tenant, userId: id })
    },
  })

  const handleCreate = async (
    values: Record<string, unknown>
  ): Promise<void> => {
    const roles = ROLE_OPTIONS.filter((r) => Boolean(values[`role_${r}`]))
    try {
      await client.adminCreateUser({
        tenantId: tenant,
        email: String(values.email ?? ''),
        name: String(values.name ?? '') || undefined,
        password: String(values.password ?? ''),
        roles,
      })
      await rs.reload()
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Create failed')
    }
  }

  const handleSaveRoles = (userId: string): void => {
    client
      .adminUpdateUser({ tenantId: tenant, userId, roles: editingRoles })
      .then(() => rs.reload())
      .then(() => setEditingId(null))
      .catch((e) => {
        setActionError(e instanceof Error ? e.message : 'Update failed')
      })
  }

  const handleConfirmReset = (userId: string): void => {
    client
      .adminUserPasswordReset({ tenantId: tenant, userId, password: resetPwd })
      .then(() => {
        setResetId(null)
        setResetPwd('')
      })
      .catch((e) => {
        setActionError(e instanceof Error ? e.message : 'Reset failed')
      })
  }

  const handleRevoke = (userId: string): void => {
    client.adminUserRevokeSessions({ tenantId: tenant, userId }).catch((e) => {
      setActionError(e instanceof Error ? e.message : 'Revoke failed')
    })
  }

  const handleConfirmDelete = async (): Promise<void> => {
    if (!pendingDelete) return
    setActionError(null)
    await rs.remove(pendingDelete.id)
    setPendingDelete(null)
  }

  const toggleEditRole = (role: string): void => {
    setEditingRoles((prev) => {
      if (prev.includes(role)) return prev.filter((r) => r !== role)
      return [...prev, role]
    })
  }

  const rowActions = (row: UserRow): ReactNode => {
    const { id, roles: userRoles, protected: isProtected } = row

    if (editingId === id) {
      return (
        <InlineEditRow>
          {ROLE_OPTIONS.map((role) => (
            <RoleCheckLabel key={role}>
              <input
                type="checkbox"
                checked={editingRoles.includes(role)}
                onChange={() => toggleEditRole(role)}
              />
              {role}
            </RoleCheckLabel>
          ))}
          <CrudSecondaryButton
            type="button"
            onClick={() => handleSaveRoles(id)}
          >
            Save Roles
          </CrudSecondaryButton>
          <CrudSecondaryButton type="button" onClick={() => setEditingId(null)}>
            Cancel
          </CrudSecondaryButton>
        </InlineEditRow>
      )
    }

    if (resetId === id) {
      const resetInputId = `rpwd-${id}`
      return (
        <InlineEditRow>
          <InlineLabel htmlFor={resetInputId}>New Password</InlineLabel>
          <InlineInput
            id={resetInputId}
            type="password"
            value={resetPwd}
            onChange={(e) => setResetPwd(e.target.value)}
          />
          <CrudSecondaryButton
            type="button"
            onClick={() => handleConfirmReset(id)}
          >
            Confirm
          </CrudSecondaryButton>
          <CrudSecondaryButton type="button" onClick={() => setResetId(null)}>
            Cancel
          </CrudSecondaryButton>
        </InlineEditRow>
      )
    }

    return (
      <CrudActionsRow>
        <CrudSecondaryButton
          type="button"
          onClick={() => {
            setEditingId(id)
            setEditingRoles([...userRoles])
          }}
        >
          Edit Roles
        </CrudSecondaryButton>
        <CrudSecondaryButton
          type="button"
          onClick={() => {
            setResetId(id)
            setResetPwd('')
          }}
        >
          Reset Password
        </CrudSecondaryButton>
        <CrudSecondaryButton type="button" onClick={() => handleRevoke(id)}>
          Revoke Sessions
        </CrudSecondaryButton>
        <CrudSecondaryButton
          type="button"
          aria-label={`View security history for ${row.email}`}
          onClick={() => setHistoryUser(row)}
        >
          Security History
        </CrudSecondaryButton>
        {/* Task 11: the genuine admin per-user factor inventory
            (UserFactors.tsx) — distinct from the self-service
            /admin/security-keys page, which only ever shows the CALLER's
            own passkeys. */}
        <CrudSecondaryButton as={Link} to={`/admin/users/${id}/factors`}>
          View Factors
        </CrudSecondaryButton>
        {!isProtected && (
          <CrudDangerButton
            type="button"
            onClick={() => {
              setActionError(null)
              setPendingDelete(row)
            }}
          >
            Delete
          </CrudDangerButton>
        )}
      </CrudActionsRow>
    )
  }

  if (tenant === 'system') {
    return (
      <PageStack>
        <TenantGateText>Please select a tenant to manage users.</TenantGateText>
      </PageStack>
    )
  }

  return (
    <PageStack>
      <PageTitle>Users</PageTitle>
      {(rs.error || actionError) && (
        <PamError message={rs.error ?? actionError ?? ''} />
      )}
      <SectionBlock>
        <SectionTitle>All Users</SectionTitle>
        <CrudTable
          columns={columns}
          items={rs.items}
          rowActions={rowActions}
          loading={rs.loading}
          caption="Users list"
          emptyMessage="No users found."
        />
      </SectionBlock>
      {historyUser && (
        <SectionBlock aria-live="polite">
          <InlineEditRow>
            <SectionTitle>Security history — {historyUser.email}</SectionTitle>
            <CrudSecondaryButton
              type="button"
              onClick={() => setHistoryUser(null)}
            >
              Close
            </CrudSecondaryButton>
          </InlineEditRow>
          <UserSecurityHistoryChart
            riskPoints={history.riskPoints}
            activeThreatPoints={history.activeThreatPoints}
            loading={history.loading}
            error={history.error}
            onRetry={() => setHistoryReload((value) => value + 1)}
          />
          {history.nextCursor && !history.error && (
            <CrudSecondaryButton
              type="button"
              disabled={history.loadingMore}
              onClick={handleLoadOlderHistory}
            >
              {history.loadingMore ? 'Loading…' : 'Load older history'}
            </CrudSecondaryButton>
          )}
        </SectionBlock>
      )}
      <SectionBlock>
        <SectionTitle>Create User</SectionTitle>
        <CrudForm
          fields={createFields}
          onSubmit={handleCreate}
          submitLabel="Create User"
        />
      </SectionBlock>

      <PamConfirmDialog
        open={pendingDelete !== null}
        title="Delete user"
        body={`Delete user "${pendingDelete?.email ?? pendingDelete?.id ?? ''}"? This cannot be undone.`}
        onConfirm={handleConfirmDelete}
        onCancel={() => setPendingDelete(null)}
        color="error"
      />
    </PageStack>
  )
}
